const localHosts = new Set(["localhost", "127.0.0.1", "[::1]"]);
const oauthProviders = new Set(["github", "gitlab", "google"]);
// A bind address is what a server sees when it is published behind a port
// mapping; the browser can never come back to it.
const bindOnlyHosts = new Set(["0.0.0.0", "[::]"]);

export function isSupportedOAuthProvider(value: unknown): value is string {
  return typeof value === "string" && oauthProviders.has(value);
}

export function authenticatedJsonHeaders(
  token: string,
  supplied?: HeadersInit,
): Headers {
  const headers = new Headers(supplied);
  headers.set("content-type", "application/json");
  headers.set("authorization", `Bearer ${token}`);
  return headers;
}

export function publicOrigin(
  requestUrl: string,
  env: Record<string, string | undefined> = process.env,
): string {
  const configured = env.CLIENT_PUBLIC_ORIGIN;
  if (!configured) {
    if (env.NODE_ENV === "production")
      throw new Error("CLIENT_PUBLIC_ORIGIN is required in production");
    const requestOrigin = new URL(requestUrl);
    if (bindOnlyHosts.has(requestOrigin.hostname.toLowerCase()))
      throw new Error(
        "CLIENT_PUBLIC_ORIGIN is required when the request host is a bind address",
      );
    return requestOrigin.origin;
  }

  const origin = new URL(configured);
  const isLocal = localHosts.has(origin.hostname.toLowerCase());
  if (origin.protocol !== "https:" && !(isLocal && origin.protocol === "http:"))
    throw new Error("CLIENT_PUBLIC_ORIGIN must use HTTPS");
  if (
    origin.username ||
    origin.password ||
    origin.pathname !== "/" ||
    origin.search ||
    origin.hash
  )
    throw new Error("CLIENT_PUBLIC_ORIGIN must contain only an origin");
  return origin.origin;
}

/**
 * A path on this origin, from a return address anyone can craft. The URL
 * parser drops tab, LF and CR and reads `\` as `/`, so `/\n/evil.example`
 * looks local and opens evil.example: those are refused outright, and the
 * parsed URL's own path is returned rather than the raw value.
 */
export function localReturnPath(value: unknown): string | null {
  // eslint-disable-next-line no-control-regex
  if (typeof value !== "string" || !/^\/(?!\/)[^\0-\x1f\x7f\\]*$/.test(value))
    return null;
  try {
    const url = new URL(value, window.location.origin);
    if (url.origin !== window.location.origin) return null;
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return null;
  }
}

export function safeExternalUrl(value: unknown): string | null {
  if (typeof value !== "string") return null;
  try {
    const url = new URL(value);
    return url.protocol === "https:" || url.protocol === "http:"
      ? url.toString()
      : null;
  } catch {
    return null;
  }
}

export function registrationPayload(value: unknown) {
  if (!value || typeof value !== "object") return null;
  const body = value as Record<string, unknown>;
  if (
    typeof body.email !== "string" ||
    typeof body.password !== "string" ||
    typeof body.firstName !== "string"
  )
    return null;
  const email = body.email.trim();
  const firstName = body.firstName.trim();
  if (!email || email.length > 320 || !firstName || firstName.length > 100)
    return null;
  if (!body.password || body.password.length > 1024) return null;
  if (body.company !== undefined && typeof body.company !== "string")
    return null;
  if (
    body.invitationToken !== undefined &&
    typeof body.invitationToken !== "string"
  )
    return null;

  return {
    email,
    password: body.password,
    firstName,
    ...(typeof body.company === "string" && body.company.trim()
      ? { company: body.company.trim().slice(0, 200) }
      : {}),
    ...(typeof body.invitationToken === "string" && body.invitationToken
      ? { invitationToken: body.invitationToken.slice(0, 512) }
      : {}),
  };
}
